import type { McpServerConfig, McpServerId } from "../../../types/mcp";
import {
  readAttachedConnectorIds,
  writeAttachedConnectorIds,
} from "../../../lib/chat-connector-attachments";
import { BrandIcon } from "../../icon-bank/BrandIcon";

export type ComposerConnectorKind =
  | "narrativeflow"
  | "riskflow"
  | "desk-plans"
  | "linear"
  | "consul"
  | "arbitrum"
  | "browser"
  | "search"
  | "data"
  | "social"
  | "trading"
  | "generic";

export interface ComposerConnectorItem {
  id: McpServerId;
  label: string;
  shortLabel: string;
  description: string;
  kind: ComposerConnectorKind;
}

interface ComposerConnectorOptions {
  allowConsulControl?: boolean;
}

const CANON_CONNECTORS: ComposerConnectorItem[] = [
  {
    id: "arbitrum",
    label: "Arbitrum",
    shortLabel: "Arbitrum",
    description: "Attach the live Arbitrum run and handoff packets",
    kind: "arbitrum",
  },
  {
    id: "narrativeflow",
    label: "NarrativeFlow",
    shortLabel: "NarrativeFlow",
    description: "Attach sessions, Docs, Timeline, Canvas, and DeskMap state",
    kind: "narrativeflow",
  },
  {
    id: "riskflow",
    label: "RiskFlow",
    shortLabel: "RiskFlow",
    description: "Attach internal catalyst headlines, risk signals, and scores",
    kind: "riskflow",
  },
  {
    id: "desk-plans",
    label: "Desk Plans",
    shortLabel: "Desk Plans",
    description: "Attach all current Desk Plan context and workflow state",
    kind: "desk-plans",
  },
  {
    id: "consul-control",
    label: "QuickScout",
    shortLabel: "QuickScout",
    description: "Attach Desktop screen and read-only app/browser control",
    kind: "consul",
  },
];

export function getCanonComposerConnectors(
  servers: McpServerConfig[] = [],
  _activeIds: McpServerId[] = [],
  options: ComposerConnectorOptions = {},
): ComposerConnectorItem[] {
  const dedupedServers = dedupeServers(servers);
  const allowConsulControl = options.allowConsulControl ?? true;
  const sourceConnectors = allowConsulControl
    ? CANON_CONNECTORS
    : CANON_CONNECTORS.filter((item) => item.id !== "consul-control");
  const canonical = sourceConnectors.map((item) => {
    const server = dedupedServers.find((candidate) => {
      const label = candidate.name.toLowerCase();
      return (
        candidate.id === item.id ||
        label === item.label.toLowerCase() ||
        (item.id === "consul-control" &&
          (label.includes("consul") || label.includes("quickscout")))
      );
    });

    if (!server) return item;
    return {
      ...item,
      label: item.label,
      description: server.description || item.description,
    };
  });
  return canonical;
}

export function readComposerConnectorIds(
  scopeId?: string | null,
): McpServerId[] {
  return readAttachedConnectorIds(scopeId);
}

export function writeComposerConnectorIds(
  nextIds: McpServerId[],
  scopeId?: string | null,
): void {
  writeAttachedConnectorIds(scopeId, nextIds);
}

export function ComposerConnectorMark({
  kind,
  className = "",
}: {
  kind: ComposerConnectorKind;
  className?: string;
}) {
  if (kind === "linear") {
    return <BrandIcon name="Linear" className={className} fallback />;
  }

  if (kind === "consul" || kind === "arbitrum") {
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12s4.48 10 10 10M13 2.05S16 6 16 12M11 21.95S8 18 8 12s3-9.95 3-9.95M2.63 15.5H12M2.63 8.5h18.74M21.88 17.92c.49.3.46 1.04-.05 1.1l-2.56.29-1.16 2.31c-.22.46-.93.24-1.04-.33l-1.26-6.12c-.1-.48.33-.78.75-.52z"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
    );
  }

  if (kind !== "narrativeflow") {
    return <ComposerPluginStackMark className={className} />;
  }

  return (
    <svg className={className} viewBox="0 0 15 15" aria-hidden="true">
      <circle
        cx="7.5"
        cy="7.5"
        r="6.623"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.95"
      />
      <path
        d="M7.6 6.01a.5.5 0 0 1 .4.49V10h1v1H6v-1h1V7H6V6h1.5zm-.1-2.26a.75.75 0 1 1 0 1.5a.75.75 0 0 1 0-1.5"
        fill="currentColor"
      />
    </svg>
  );
}

function dedupeServers(servers: McpServerConfig[]): McpServerConfig[] {
  const seen = new Set<string>();
  const deduped: McpServerConfig[] = [];
  for (const server of servers) {
    if (seen.has(server.id)) continue;
    seen.add(server.id);
    deduped.push(server);
  }
  return deduped;
}

export function ComposerRemoveMark({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 15 15" aria-hidden="true">
      <path
        fill="currentColor"
        d="M10.969 3.219a.574.574 0 1 1 .812.812L8.313 7.5l3.468 3.469l.074.09a.575.575 0 0 1-.796.796l-.09-.074L7.5 8.312l-3.469 3.47a.574.574 0 1 1-.812-.813L6.688 7.5l-3.47-3.469l-.073-.09a.575.575 0 0 1 .796-.797l.09.075L7.5 6.687z"
      />
    </svg>
  );
}

export function ComposerPluginStackMark({
  className = "",
}: {
  className?: string;
}) {
  return (
    <svg className={className} viewBox="0 0 15 15" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.2"
        d="M5.15 1.8h4.7v2.45h2.45v4.7H9.85v2.45h-4.7V8.95H2.7v-4.7h2.45z"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1"
        d="M5.15 4.25h4.7M5.15 8.95h4.7"
      />
    </svg>
  );
}
